import React, { useState, useRef, useEffect, memo } from "react";
import { createPortal } from "react-dom";
import { createRoot, type Root } from "react-dom/client";
import { motion, AnimatePresence } from "framer-motion";
import {
  useOsOpenApps,
  useOsActions,
  useOsSettings,
  useOsDockTooltips,
  useOsWindowList,
  type WindowMenuInfo,
  type OsWindow,
} from "./OsContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGrid2 } from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import { faSearch } from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import {
  APP_REGISTRY,
  filterAppsByPermission,
} from "./OsAppRegistry";
import { getRelatedAppIdsForOsDock } from "./osDockAppGrouping";
import { getOsGlassSurfaceClassName } from "./osWindowChrome";
import {
  getDockIconBackgroundClasses,
  DOCK_OPEN_INDICATOR_ACTIVE_CLASS,
  DOCK_OPEN_INDICATOR_IDLE_CLASS,
  DOCK_TILE_SHELL_CLASSES,
  DOCK_CHROME_RADIUS_CLASS,
  DOCK_ICON_TILE_CLASS,
  DOCK_BUTTON_BASE_CLASS,
  getDockButtonStateClasses,
  DOCK_CHROME_GLASS_CLASSES,
  DOCK_CHROME_FRAME_CLASSES,
} from "./osDockIconTheme";
import { getOsAppsPopoverAppIds } from "./osDockPopoverApps";
export { ensureMinesweeperLast } from "./osDockPopoverApps";
import { preloadOsApp, scheduleIdleOsAppPrefetch } from "./osAppPrefetch";
import {
  APPS_POPOVER_CELL_PX,
  APPS_POPOVER_GAP_PX,
  APPS_POPOVER_PAD_PX,
  getAppsPopoverCenterX,
  getAppsPopoverLayout,
} from "./osDockPopoverLayout";
import { usePlatform } from "@/hooks/usePlatform";
import { getShortcutLabel } from "./osShortcuts";
import { useAuth } from "@/context/AuthContext";
import { useModulePermissions } from "@/context/ModulePermissionsContext";
import { useOsUserSettings } from "@/hooks/useOsUserSettings";
import {
  filterOpenworkExcludedAppIds,
  isOpenworkCompany,
  resolveDockAppLabel,
  resolveDockBaseAppIds,
  buildOpenworkAppsPopoverEntries,
  type OpenworkAppsPopoverEntry,
} from "./openworkDockApps";
import { filterSaltusHiddenAppIds, isSaltusCompany } from "./saltusDockApps";
import tippy, { type Instance } from "tippy.js";
import "tippy.js/dist/tippy.css";
import "tippy.js/animations/shift-away.css";
import "./OsDock.css";

const ANI_DISCOVERABILITY_KEY = "ani_discoverability_seen";

function hasSeenAniDiscoverability(): boolean {
  if (typeof window === "undefined") return true;
  return localStorage.getItem(ANI_DISCOVERABILITY_KEY) === "1";
}

function markAniDiscoverabilitySeen(): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(ANI_DISCOVERABILITY_KEY, "1");
}

interface HTMLElementWithTippy extends HTMLElement {
  _tippy?: Instance;
}

// Dock icon backgrounds live in osDockIconTheme.ts (shared with Apps popover + DockStyleAppIcon).

/**
 * Dock icon appIds that group related window appIds (e.g. workers + worker).
 */
function getRelatedDockAppIds(appId: string): string[] {
  if (appId === "workers") return ["workers", "worker"];
  if (appId === "reports") return ["reports", "report-builder"];
  if (appId === "documents") return ["documents", "document"];
  return [appId];
}

export function focusTopmostAppWindowInWorkspace(
  windowIds: string[],
  workspaceId: string,
  getWindow: (windowId: string) => OsWindow | undefined,
  focusWindow: (windowId: string) => void,
): boolean {
  let topWindowId: string | undefined;
  let topZIndex = -Infinity;
  for (const windowId of windowIds) {
    const window = getWindow(windowId);
    if (!window || window.workspaceId !== workspaceId) continue;
    if (window.zIndex > topZIndex) {
      topZIndex = window.zIndex;
      topWindowId = windowId;
    }
  }
  if (topWindowId) {
    focusWindow(topWindowId);
    return true;
  }
  return false;
}

function dockAppIsFocused(
  dockAppId: string,
  focusedAppId: string | null,
): boolean {
  if (!focusedAppId) return false;
  return getRelatedDockAppIds(dockAppId).includes(focusedAppId);
}

interface DockContextMenuProps {
  appId: string;
  hasOpenWindows: boolean;
  menuWindows: WindowMenuInfo[];
  onShowAllWindows: () => void;
  onHide: () => void;
  onQuit: () => void;
  onOpen: () => void;
  onFocusWindow: (windowId: string) => void;
  isPinned?: boolean;
  canPin?: boolean;
  onTogglePin?: () => void;
  onHideFromDock?: () => void;
}

/**
 * Renders the context menu content for dock app icons
 */
const DockContextMenuContent: React.FC<DockContextMenuProps> = ({
  appId: _appId,
  hasOpenWindows,
  menuWindows,
  onShowAllWindows,
  onHide,
  onQuit,
  onOpen,
  onFocusWindow,
  isPinned,
  canPin,
  onTogglePin,
  onHideFromDock,
}) => {
  const showPinSection = canPin && (onTogglePin || onHideFromDock);
  return (
    <div className="py-1 bg-white/95 dark:bg-slate-900/60 backdrop-blur-sm rounded-2xl shadow-2xl p-1.5 py-[6px] flex flex-col overflow-hidden min-w-[200px]">
      {hasOpenWindows ? (
        <>
          {/* List individual windows if there are any */}
          {menuWindows.length >= 1 && (
            <>
              {menuWindows.map((win) => (
                <button
                  key={win.id}
                  onClick={() => onFocusWindow(win.id)}
                  className="w-full flex items-center gap-3 px-2 py-2 hover:bg-slate-900/40 transition-colors text-left rounded-xl"
                >
                  <span className="text-sm text-slate-700 dark:text-slate-200 truncate">
                    {win.title}
                  </span>
                </button>
              ))}
              <div className="h-px bg-slate-200 dark:bg-slate-700 my-2" />
            </>
          )}

          <button
            onClick={onShowAllWindows}
            className="w-full flex items-center gap-3 px-2 py-2 hover:bg-slate-900/40 transition-colors text-left rounded-xl"
          >
            <span className="text-sm text-slate-700 dark:text-slate-200">
              Show all windows
            </span>
          </button>
          <button
            onClick={onHide}
            className="w-full flex items-center gap-3 px-2 py-2 hover:bg-slate-900/40 transition-colors text-left rounded-xl"
          >
            <span className="text-sm text-slate-700 dark:text-slate-200">
              Hide
            </span>
          </button>
          <button
            onClick={onQuit}
            className="w-full flex items-center gap-3 px-2 py-2 hover:bg-slate-900/40 transition-colors text-left rounded-xl"
          >
            <span className="text-sm text-slate-700 dark:text-slate-200">
              Quit
            </span>
          </button>
        </>
      ) : (
        <button
          onClick={onOpen}
          className="w-full flex items-center gap-3 px-2 py-2 hover:bg-slate-900/40 transition-colors text-left rounded-xl"
        >
          <span className="text-sm text-slate-700 dark:text-slate-200">
            Open
          </span>
        </button>
      )}

      {showPinSection && (
        <>
          <div className="h-px bg-slate-200 dark:bg-slate-700 my-2" />
          {onTogglePin && (
            <button
              onClick={onTogglePin}
              className="w-full flex items-center gap-3 px-2 py-2 hover:bg-slate-900/40 transition-colors text-left rounded-xl"
            >
              <span className="text-sm text-slate-700 dark:text-slate-200">
                {isPinned ? "Unpin from Dock" : "Pin to Dock"}
              </span>
            </button>
          )}
          {onHideFromDock && (
            <button
              onClick={onHideFromDock}
              className="w-full flex items-center gap-3 px-2 py-2 hover:bg-slate-900/40 transition-colors text-left rounded-xl"
            >
              <span className="text-sm text-slate-700 dark:text-slate-200">
                Remove from Dock
              </span>
            </button>
          )}
        </>
      )}
    </div>
  );
};

const AppsDockPopover: React.FC<{
  onClose: () => void;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  dockIconStyle?: "gradient" | "colored";
  /** Apps already shown on the dock — excluded from the grid. */
  excludeAppIds?: string[];
  companyId?: string | null;
}> = ({ onClose, buttonRef, dockIconStyle = "gradient", excludeAppIds, companyId }) => {
  const { isAppOpenInWorkspace, activeWorkspaceId, getWindowIdsForApp } =
    useOsOpenApps();
  const { getWindow } = useOsWindowList();
  const { openWindow, focusWindow } = useOsActions();
  // Dock pins use strict membership; Apps grid uses effective access (company
  // cap + permissions), so new modules like calculators appear when granted.
  const { hasPermission } = useModulePermissions();
  const [position, setPosition] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isVisible, setIsVisible] = React.useState(false);
  const [viewportWidth, setViewportWidth] = React.useState(
    () => (typeof window === "undefined" ? 1280 : window.innerWidth),
  );

  // Filter apps by effective access; list order from getOsAppsPopoverAppIds (Minesweeper last)
  const popoverEntries = React.useMemo((): OpenworkAppsPopoverEntry[] => {
    const ids = filterAppsByPermission(
      getOsAppsPopoverAppIds(excludeAppIds ?? []),
      hasPermission,
    );
    const clickable = isOpenworkCompany(companyId)
      ? filterOpenworkExcludedAppIds(ids)
      : isSaltusCompany(companyId)
        ? filterSaltusHiddenAppIds(ids)
        : ids;
    return isOpenworkCompany(companyId)
      ? buildOpenworkAppsPopoverEntries(clickable, excludeAppIds ?? [])
      : clickable.map((appId) => ({ appId, marketing: false }));
  }, [hasPermission, excludeAppIds, companyId]);

  React.useEffect(() => {
    const updatePosition = () => {
      setViewportWidth(window.innerWidth);
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setPosition({
          x: rect.left + rect.width / 2,
          y: rect.top,
        });
      }
    };

    updatePosition();
    // Trigger animation after position is set
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    });

    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [buttonRef]);

  const handleAppClick = (appId: string) => {
    const appDef = APP_REGISTRY[appId];
    if (!appDef) return;

    void preloadOsApp(appId);

    const isOpenInWorkspace = isAppOpenInWorkspace(appId, activeWorkspaceId);
    const windowIds = getWindowIdsForApp(appId);

    if (isOpenInWorkspace && windowIds.length > 0) {
      focusTopmostAppWindowInWorkspace(
        windowIds,
        activeWorkspaceId,
        getWindow,
        focusWindow,
      );
    } else {
      openWindow({
        appId,
        title: appDef.label,
      });
    }

    onClose();
  };

  // Don't render until position is calculated
  if (!position) return null;

  const visibleEntries = popoverEntries.filter(
    (entry) => APP_REGISTRY[entry.appId],
  );

  const { columnCount, widthPx } = getAppsPopoverLayout(
    visibleEntries.length,
    viewportWidth,
  );
  const centerX = getAppsPopoverCenterX(position.x, widthPx, viewportWidth);

  const popoverContent = (
    <div
      data-apps-popover
      data-testid="apps-popover"
      className={`fixed box-border max-h-[432px] overflow-y-auto overflow-x-hidden ${DOCK_CHROME_RADIUS_CLASS} transition-all duration-200 ease-out scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-700 ${DOCK_CHROME_FRAME_CLASSES} ${getOsGlassSurfaceClassName(
        DOCK_CHROME_GLASS_CLASSES,
      )}`}
      style={{
        left: centerX,
        width: widthPx,
        bottom: window.innerHeight - position.y + 12,
        transform: `translateX(-50%) translateY(${isVisible ? "0" : "8px"})`,
        opacity: isVisible ? 1 : 0,
        zIndex: 9999,
        boxSizing: "border-box",
        display: "grid",
        gridTemplateColumns: `repeat(${columnCount}, ${APPS_POPOVER_CELL_PX}px)`,
        gap: `${APPS_POPOVER_GAP_PX}px`,
        padding: `${APPS_POPOVER_PAD_PX}px`,
      }}
    >
        {visibleEntries.map(({ appId, marketing }) => {
          const app = APP_REGISTRY[appId];
          if (!app) return null;
          const appLabel = resolveDockAppLabel(appId, app.label, companyId);

          const iconCell = (() => {
            const { classes } = getDockIconBackgroundClasses(
              app.colorTheme,
              false,
              dockIconStyle,
            );
            return (
              <div
                className={`${DOCK_ICON_TILE_CLASS} ${classes} ${
                  marketing ? "opacity-45 saturate-50" : "cursor-pointer"
                }`}
              >
                {app.icon}
              </div>
            );
          })();

          if (marketing) {
            return (
              <div
                key={appId}
                role="presentation"
                data-testid={`apps-popover-marketing-${appId}`}
                className="flex w-full cursor-not-allowed flex-col items-center gap-1.5"
              >
                {iconCell}
                <span className="w-full truncate text-center text-[10px] leading-none text-slate-400 dark:text-white/35">
                  {appLabel}
                </span>
              </div>
            );
          }

          return (
            <button
              key={appId}
              type="button"
              onMouseEnter={() => {
                void preloadOsApp(appId);
              }}
              onPointerDown={() => {
                void preloadOsApp(appId);
              }}
              onClick={() => handleAppClick(appId)}
              className="flex w-full cursor-pointer flex-col items-center gap-1.5 outline-none transition-colors focus:outline-none focus-visible:outline-none"
            >
              {iconCell}
              <span className="w-full truncate text-center text-[10px] leading-none text-slate-600 dark:text-white/70">
                {appLabel}
              </span>
            </button>
          );
        })}
    </div>
  );

  return createPortal(popoverContent, document.body);
};

// Dock button configuration
interface DockButtonConfig {
  id: string;
  type: "app" | "special";
  icon?: React.ReactNode;
  label: string;
  onClick?: () => void;
  getBadgeCount?: () => number;
  showOpenIndicator?: boolean;
  getIsActive?: () => boolean;
  /** Optional test id for the dock control button */
  dataTestId?: string;
  className?: string;
  colorTheme?: {
    type:
      | "gradient"
      | "solid"
      | "glass"
      | "bordered"
      | "radial"
      | "conic"
      | "mesh";
    colors: string;
    iconColor: string;
    shadow?: string;
  };
}

// Helper component for rendering dock buttons
const DockButton: React.FC<{
  config: DockButtonConfig;
  isOpen: boolean;
  onClick: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  buttonRef?: React.MutableRefObject<HTMLButtonElement | null>;
  dockIconStyle?: "gradient" | "colored";
  appId?: string;
  onShowAllWindows?: (appId: string) => void;
  onHideApp?: (appId: string) => void;
  onQuitApp?: (appId: string) => void;
  onOpenApp?: (appId: string) => void;
  onFocusWindow?: (windowId: string) => void;
  menuWindows?: WindowMenuInfo[];
  hasOpenWindows?: boolean;
  /** When true, open-indicator uses indigo (frontmost app in workspace); otherwise muted */
  isFocusedApp?: boolean;
}> = ({
  config,
  isOpen,
  onClick,
  onContextMenu: _onContextMenu,
  buttonRef,
  dockIconStyle,
  appId,
  onShowAllWindows,
  onHideApp,
  onQuitApp,
  onOpenApp,
  onFocusWindow,
  menuWindows,
  hasOpenWindows,
  isFocusedApp = false,
}) => {
  const badgeCount = config.getBadgeCount?.() ?? 0;
  const isActive = config.getIsActive?.() ?? false;
  const contextMenuRef = useRef<Instance | null>(null);
  const latestContextMenuPropsRef = useRef<{
    appId?: string;
    hasOpenWindows: boolean;
    menuWindows: WindowMenuInfo[];
    onShowAllWindows?: (appId: string) => void;
    onHideApp?: (appId: string) => void;
    onQuitApp?: (appId: string) => void;
    onOpenApp?: (appId: string) => void;
    onFocusWindow?: (windowId: string) => void;
  } | null>(null);

  latestContextMenuPropsRef.current = {
    appId,
    hasOpenWindows: hasOpenWindows ?? false,
    menuWindows: menuWindows ?? [],
    onShowAllWindows,
    onHideApp,
    onQuitApp,
    onOpenApp,
    onFocusWindow,
  };

  return (
    <motion.div
      className="relative"
      layout
      initial={{ scale: 1, opacity: 1 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{
        duration: 0.2,
        ease: "easeInOut",
      }}
    >
      <button
        type="button"
        data-testid={config.dataTestId}
        data-dock-button-id={config.id}
        ref={(el) => {
          if (el && !el.hasAttribute("data-tippy-initialized")) {
            tippy(el, {
              content: config.label,
              delay: [600, 0],
              placement: "top",
              arrow: false,
              animation: "shift-away",
              theme: "dock",
              offset: [0, 16],
            });
            el.setAttribute("data-tippy-initialized", "true");
          }

          // Setup context menu if appId is provided
          if (
            el &&
            appId &&
            onShowAllWindows &&
            onHideApp &&
            onQuitApp &&
            onOpenApp &&
            menuWindows &&
            !el.hasAttribute("data-context-menu-initialized")
          ) {
            const container = document.createElement("div");
            let currentRoot: Root | null = null;

            contextMenuRef.current = tippy(el, {
              content: container,
              trigger: "manual",
              placement: "top",
              arrow: false,
              animation: "shift-away",
              theme: "context-menu",
              offset: [0, 16],
              interactive: true,
              appendTo: document.body,
              onShow(instance) {
                const latest = latestContextMenuPropsRef.current;
                const latestAppId = latest?.appId;
                if (!latestAppId) return false;

                // Clean up previous root if exists
                if (currentRoot) {
                  currentRoot.unmount();
                }

                currentRoot = createRoot(container);
                currentRoot.render(
                  <DockContextMenuContent
                    appId={latestAppId}
                    hasOpenWindows={latest.hasOpenWindows}
                    menuWindows={latest.menuWindows}
                    onShowAllWindows={() => {
                      latest.onShowAllWindows?.(latestAppId);
                      instance.hide();
                    }}
                    onHide={() => {
                      latest.onHideApp?.(latestAppId);
                      instance.hide();
                    }}
                    onQuit={() => {
                      latest.onQuitApp?.(latestAppId);
                      instance.hide();
                    }}
                    onOpen={() => {
                      latest.onOpenApp?.(latestAppId);
                      instance.hide();
                    }}
                    onFocusWindow={(windowId) => {
                      latest.onFocusWindow?.(windowId);
                      instance.hide();
                    }}
                  />,
                );
              },
              onHide(_instance) {
                if (currentRoot) {
                  // Don't block - unmount immediately after hide starts
                  currentRoot.unmount();
                  currentRoot = null;
                }
              },
              onClickOutside(instance) {
                instance.hide();
              },
            });

            el.addEventListener("contextmenu", (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (contextMenuRef.current) {
                contextMenuRef.current.show();
              }
            });

            el.setAttribute("data-context-menu-initialized", "true");
          }

          if (buttonRef) {
            buttonRef.current = el;
          }
        }}
        onClick={onClick}
        onMouseEnter={() => {
          if (appId) {
            void preloadOsApp(appId);
          }
        }}
        onPointerDown={() => {
          if (appId) {
            void preloadOsApp(appId);
          }
        }}
        className={`${DOCK_BUTTON_BASE_CLASS} ${getDockButtonStateClasses(isActive)} ${config.className || ""}`}
      >
        {(() => {
          const { classes, style } = getDockIconBackgroundClasses(
            config.colorTheme,
            config.id === "search",
            dockIconStyle,
          );
          return (
            <div
              className={`${DOCK_ICON_TILE_CLASS} cursor-pointer relative ${classes}`}
              style={style}
            >
              {config.icon}
              {badgeCount > 0 && (
                <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-[10px] font-semibold text-white leading-none">
                    {badgeCount > 99 ? "99+" : badgeCount}
                  </span>
                </div>
              )}
            </div>
          );
        })()}

        {config.showOpenIndicator && isOpen ? (
          <div
            className={`absolute -bottom-[6px] h-[3px] w-3 rounded-full ${
              isFocusedApp
                ? DOCK_OPEN_INDICATOR_ACTIVE_CLASS
                : DOCK_OPEN_INDICATOR_IDLE_CLASS
            }`}
          />
        ) : null}
      </button>
    </motion.div>
  );
};

export const OsDock: React.FC<{
  onAniClick?: () => void;
  isAniOpen?: boolean;
  openSearch: () => void;
}> = memo(function OsDock({ onAniClick, isAniOpen = false, openSearch }) {
  const {
    openAppIds,
    activeWorkspaceId,
    isAppOpen,
    isAppOpenInWorkspace,
    getWindowIdsForApp,
    getWindowsForContextMenu,
    focusedAppIdInActiveWorkspace,
  } = useOsOpenApps();
  const { getWindow } = useOsWindowList();
  const {
    openWindow,
    focusWindow,
    switchWorkspace,
    updateWindow,
    closeWindow,
  } = useOsActions();
  const { dockIconStyle } = useOsSettings();
  const { getTooltip } = useOsDockTooltips();
  const { isMac } = usePlatform();
  // Use strict module-membership for the dock so admins still see only the apps
  // explicitly assigned to them. (Route/API gates elsewhere keep using
  // hasPermission, which honours `*`/`admin:*`.)
  const { hasModule } = useModulePermissions();
  const { user } = useAuth();
  const companyId = user?.company_id;
  const {
    settings: osUserSettings,
    pinApp,
    unpinApp,
    hideApp,
  } = useOsUserSettings();
  const pinnedAppIds = osUserSettings.pinnedAppIds;
  const hiddenAppIds = osUserSettings.hiddenAppIds;
  const [appsPopoverOpen, setAppsPopoverOpen] = useState(false);
  const [showAniPulse, setShowAniPulse] = useState(() => !hasSeenAniDiscoverability());
  const appsButtonRef = useRef<HTMLButtonElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const aniButtonRef = useRef<HTMLButtonElement | null>(null);
  const dynamicDockContextMenuRef = useRef<
    Map<string, { hasOpenWindows: boolean; menuWindows: WindowMenuInfo[] }>
  >(new Map());
  const dynamicDockActionsRef = useRef<{
    handleShowAllWindows: (appId: string) => void;
    handleHideApp: (appId: string) => void;
    handleQuitApp: (appId: string) => void;
    handleAppClick: (appId: string) => void;
    focusWindow: (windowId: string) => void;
  } | null>(null);

  useEffect(() => {
    if (!appsPopoverOpen) return;
    const handleClick = (e: MouseEvent) => {
      // Check if click is on the button or inside any element with data-apps-popover attribute
      const target = e.target as HTMLElement;
      const isInsidePopover = target.closest("[data-apps-popover]");
      const isButton =
        appsButtonRef.current && appsButtonRef.current.contains(target);

      if (!isInsidePopover && !isButton) {
        setAppsPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [appsPopoverOpen]);

  // Update search button tooltip when isMac changes
  useEffect(() => {
    if (searchButtonRef.current) {
      const button = searchButtonRef.current as HTMLElementWithTippy;
      if (button._tippy) {
        button._tippy.setContent(
          `Search (${getShortcutLabel("TOGGLE_GLOBAL_SEARCH", isMac)})`,
        );
      }
    }
  }, [isMac]);

  // Update Ani dock tooltip when isMac changes (matches search shortcut pattern)
  useEffect(() => {
    if (aniButtonRef.current) {
      const button = aniButtonRef.current as HTMLElementWithTippy;
      if (button._tippy) {
        button._tippy.setContent(
          `Ani Assistant (${getShortcutLabel("TOGGLE_ANI_ASSISTANT", isMac)})`,
        );
      }
    }
  }, [isMac]);

  const handleAppClick = (appId: string) => {
    const appDef = APP_REGISTRY[appId];
    if (!appDef) return;

    void preloadOsApp(appId);

    const windowIds = getWindowIdsForApp(appId);
    const menuWindows = getWindowsForContextMenu(appId);
    const isOpenInCurrentWorkspace = isAppOpenInWorkspace(
      appId,
      activeWorkspaceId,
    );
    const isOpenAnywhere = isAppOpen(appId);

    if (isOpenInCurrentWorkspace && windowIds.length > 0) {
      focusTopmostAppWindowInWorkspace(
        windowIds,
        activeWorkspaceId,
        getWindow,
        focusWindow,
      );
    } else if (isOpenAnywhere && menuWindows.length > 0) {
      const windowInOtherWorkspace = menuWindows.find(
        (w) => w.workspaceId !== activeWorkspaceId,
      );
      if (windowInOtherWorkspace) {
        switchWorkspace(windowInOtherWorkspace.workspaceId);
        focusWindow(windowInOtherWorkspace.id);
      }
    } else {
      openWindow({
        appId,
        title: appDef.label,
      });
    }
  };

  const getAllWindowIdsForQuitOrHide = (appId: string): string[] => {
    const relatedAppIds = getRelatedAppIdsForOsDock(appId);
    return relatedAppIds.flatMap((id) => getWindowIdsForApp(id));
  };

  const getAllMenuWindowsForApp = (appId: string): WindowMenuInfo[] => {
    const relatedAppIds = getRelatedAppIdsForOsDock(appId);
    return relatedAppIds.flatMap((id) => getWindowsForContextMenu(id));
  };

  const handleShowAllWindows = (appId: string) => {
    const menuWindows = getAllMenuWindowsForApp(appId);
    let targetWorkspaceId = activeWorkspaceId;

    menuWindows.forEach((win) => {
      if (win.workspaceId !== targetWorkspaceId) {
        switchWorkspace(win.workspaceId);
        targetWorkspaceId = win.workspaceId;
      }
    });

    if (menuWindows.length > 0) {
      const relatedAppIds = getRelatedAppIdsForOsDock(appId);
      const windowIds = relatedAppIds.flatMap((id) => getWindowIdsForApp(id));
      focusTopmostAppWindowInWorkspace(
        windowIds,
        targetWorkspaceId,
        getWindow,
        focusWindow,
      );
    }
  };

  const handleHideApp = (appId: string) => {
    const windowIds = getAllWindowIdsForQuitOrHide(appId);
    windowIds.forEach((windowId) => {
      updateWindow(windowId, { isMinimized: true });
    });
  };

  const handleQuitApp = (appId: string) => {
    const windowIds = getAllWindowIdsForQuitOrHide(appId);
    windowIds.forEach((windowId) => {
      closeWindow(windowId);
    });
  };

  dynamicDockActionsRef.current = {
    handleShowAllWindows,
    handleHideApp,
    handleQuitApp,
    handleAppClick,
    focusWindow,
  };

  // Get all unique app IDs that should appear in the dock
  // Show ALL open apps regardless of workspace (global dock state)
  // Filter by permissions and per-user pinned/hidden settings
  const dockAppIds = React.useMemo(() => {
    const baseAppIds = resolveDockBaseAppIds(companyId, pinnedAppIds);

    // Separate inbox and dashboard from other dock apps (they're in special buttons)
    const dockAppsWithoutSpecial = baseAppIds.filter(
      (id) => id !== "inbox" && id !== "dashboard" && id !== "home",
    );

    // Strict module membership — OpenWork dock order/exclusions come from resolveDockBaseAppIds.
    const permittedDockApps = filterAppsByPermission(
      dockAppsWithoutSpecial,
      hasModule,
    );

    // Combine permitted dock apps with open apps, removing duplicates while preserving order
    const combined = [...permittedDockApps];
    // Apps without a module that are still allowed in the dock (utility / fallback views).
    // Anything else without a module should NOT bypass the permission filter.
    const NO_MODULE_DOCK_ALLOWLIST = new Set(["minesweeper"]);
    openAppIds.forEach((appId) => {
      // Exclude special dock buttons and individual windows that are grouped under their parent app
      if (
        !combined.includes(appId) &&
        appId !== "inbox" &&
        appId !== "dashboard" &&
        appId !== "home" &&
        appId !== "worker" && // Individual worker windows grouped under "workers"
        appId !== "worker-create" && // Worker creation windows don't need dock icon
        appId !== "report-builder" && // Listed in DOCK_APPS; avoid duplicate from openAppIds
        appId !== "document" && // Editor windows roll up under the documents dock icon
        appId !== "scenario-metric-detail" &&
        appId !== "scenario-article-detail"
      ) {
        const app = APP_REGISTRY[appId];
        if (!app) return;
        const allowed = app.module
          ? hasModule(app.module)
          : NO_MODULE_DOCK_ALLOWLIST.has(appId);
        if (allowed) {
          combined.push(appId);
        }
      }
    });

    // Apply per-user hidden filter, but never hide currently-open apps so the
    // user always has a way to get back to them.
    const hiddenSet = new Set(hiddenAppIds);
    const visible = combined.filter((id) => !hiddenSet.has(id) || openAppIds.has(id));
    if (isOpenworkCompany(companyId)) {
      return filterOpenworkExcludedAppIds(visible);
    }
    if (isSaltusCompany(companyId)) {
      return filterSaltusHiddenAppIds(visible);
    }
    return visible;
  }, [openAppIds, hasModule, pinnedAppIds, hiddenAppIds, companyId]);

  useEffect(() => {
    return scheduleIdleOsAppPrefetch(dockAppIds);
  }, [dockAppIds]);

  // Build special dock buttons configuration - filter by permissions
  const specialButtons: DockButtonConfig[] = React.useMemo(() => {
    const buttons: DockButtonConfig[] = [
      {
        id: "search",
        type: "special",
        icon: <FontAwesomeIcon icon={faSearch} className="text-xl" />,
        label: `Search (${getShortcutLabel("TOGGLE_GLOBAL_SEARCH", isMac)})`,
        onClick: openSearch,
        colorTheme: {
          type: "solid",
          colors: "bg-slate-900/40",
          iconColor: "text-slate-400",
        },
      },
    ];

    if (hasModule("home")) {
      buttons.push({
        id: "home",
        type: "app",
        icon: APP_REGISTRY["home"]?.icon,
        label: APP_REGISTRY["home"]?.label || "Home",
        showOpenIndicator: true,
        colorTheme: APP_REGISTRY["home"]?.colorTheme,
      });
    }

    // Only add dashboard if user has the dashboard module assigned
    if (hasModule("dashboard")) {
      buttons.push({
        id: "dashboard",
        type: "app",
        icon: APP_REGISTRY["dashboard"]?.icon,
        label: APP_REGISTRY["dashboard"]?.label || "Dashboard",
        showOpenIndicator: true,
        colorTheme: APP_REGISTRY["dashboard"]?.colorTheme,
      });
    }

    // Inbox button disabled for now
    // buttons.push({
    //   id: "inbox",
    //   type: "app",
    //   icon: APP_REGISTRY["inbox"]?.icon,
    //   label: APP_REGISTRY["inbox"]?.label || "Inbox",
    //   showOpenIndicator: true,
    //   colorTheme: APP_REGISTRY["inbox"]?.colorTheme,
    //   getBadgeCount: () => unreadCount,
    // });

    return buttons;
  }, [isMac, hasModule, openSearch]);

  return (
    <div className="pointer-events-none relative bottom-0 z-50 flex justify-center pb-4">
      <motion.div
        layout
        className={`pointer-events-auto flex items-center gap-3 ${DOCK_CHROME_RADIUS_CLASS} px-3 py-1.5 ${DOCK_CHROME_FRAME_CLASSES} ${getOsGlassSurfaceClassName(
          DOCK_CHROME_GLASS_CLASSES,
        )}`}
        transition={{
          layout: { duration: 0.3, ease: "easeInOut" },
        }}
      >
        {/* Special buttons (Search, Dashboard & Inbox) */}
        {specialButtons.map((buttonConfig) => {
          const appIdForButton =
            buttonConfig.type === "app" ? buttonConfig.id : undefined;
          const isAppOpenForButton = appIdForButton
            ? isAppOpen(appIdForButton)
            : false;

          // Determine click handler - use stored onClick for search, handleAppClick for apps
          const clickHandler = buttonConfig.onClick
            ? buttonConfig.onClick
            : () => handleAppClick(buttonConfig.id);

          const menuWindowsForButton = appIdForButton
            ? getAllMenuWindowsForApp(appIdForButton)
            : [];

          const dockIconFocused =
            appIdForButton != null
              ? dockAppIsFocused(
                  appIdForButton,
                  focusedAppIdInActiveWorkspace,
                )
              : false;

          return (
            <DockButton
              key={buttonConfig.id}
              config={buttonConfig}
              isOpen={isAppOpenForButton}
              onClick={clickHandler}
              buttonRef={
                buttonConfig.id === "search" ? searchButtonRef : undefined
              }
              dockIconStyle={dockIconStyle}
              appId={appIdForButton}
              onShowAllWindows={handleShowAllWindows}
              onHideApp={handleHideApp}
              onQuitApp={handleQuitApp}
              onOpenApp={handleAppClick}
              onFocusWindow={focusWindow}
              menuWindows={menuWindowsForButton}
              hasOpenWindows={isAppOpenForButton}
              isFocusedApp={dockIconFocused}
            />
          );
        })}

        {/* Dynamic dock apps */}
        <AnimatePresence mode="popLayout">
          {dockAppIds.map((appId) => {
            const app = APP_REGISTRY[appId];
            if (!app) return null;
            const appLabel = resolveDockAppLabel(appId, app.label, companyId);

            // Check if app is open in ANY workspace (global state)
            // Uses helper to include related apps (workers/worker, reports/report-builder, etc.)
            const menuWindowsForApp = getAllMenuWindowsForApp(appId);
            const appIsOpen = menuWindowsForApp.length > 0;
            const appIsFocused = dockAppIsFocused(
              appId,
              focusedAppIdInActiveWorkspace,
            );
            dynamicDockContextMenuRef.current.set(appId, {
              hasOpenWindows: appIsOpen,
              menuWindows: menuWindowsForApp,
            });

            return (
              <motion.div
                key={appId}
                className="relative"
                layout
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{
                  layout: { duration: 0.3, ease: "easeInOut" },
                  duration: 0.2,
                  ease: "easeInOut",
                }}
              >
                <button
                  type="button"
                  data-dock-app-id={appId}
                  ref={(el) => {
                    if (el && !el.hasAttribute("data-tippy-initialized")) {
                      tippy(el, {
                        content: appLabel,
                        delay: [300, 0],
                        placement: "top",
                        arrow: false,
                        animation: "shift-away",
                        theme: "dock",
                        offset: [0, 16],
                        onShow(instance) {
                          const dockAppId = el.getAttribute("data-dock-app-id");
                          if (!dockAppId) return;

                          // Priority: 1) Custom tooltip from context, 2) Single window title, 3) App label
                          const customTooltip = getTooltip(dockAppId);
                          if (customTooltip) {
                            instance.setContent(customTooltip);
                            return;
                          }

                          // Check for single window scenario
                          const menuWindows = getAllMenuWindowsForApp(dockAppId);
                          if (menuWindows.length === 1) {
                            instance.setContent(menuWindows[0].title);
                            return;
                          }

                          // Default to app label
                          const appDef = APP_REGISTRY[dockAppId];
                          instance.setContent(appDef?.label || dockAppId);
                        },
                      });
                      el.setAttribute("data-tippy-initialized", "true");
                    }

                    // Setup context menu - only initialize once
                    if (
                      el &&
                      !el.hasAttribute("data-context-menu-initialized")
                    ) {
                      const container = document.createElement("div");
                      let currentRoot: Root | null = null;

                      const contextMenuInstance = tippy(el, {
                        content: container,
                        trigger: "manual",
                        placement: "top",
                        arrow: false,
                        animation: "shift-away",
                        theme: "context-menu",
                        offset: [0, 18],
                        interactive: true,
                        appendTo: document.body,
                        onShow(instance) {
                          const latestMenuState =
                            dynamicDockContextMenuRef.current.get(appId) ?? {
                              hasOpenWindows: appIsOpen,
                              menuWindows: menuWindowsForApp,
                            };
                          const latestActions = dynamicDockActionsRef.current;

                          if (currentRoot) {
                            currentRoot.unmount();
                          }

                          const isPinned = pinnedAppIds.includes(appId);
                          currentRoot = createRoot(container);
                          currentRoot.render(
                            <DockContextMenuContent
                              appId={appId}
                              hasOpenWindows={latestMenuState.hasOpenWindows}
                              menuWindows={latestMenuState.menuWindows}
                              onShowAllWindows={() => {
                                latestActions?.handleShowAllWindows(appId);
                                instance.hide();
                              }}
                              onHide={() => {
                                latestActions?.handleHideApp(appId);
                                instance.hide();
                              }}
                              onQuit={() => {
                                latestActions?.handleQuitApp(appId);
                                instance.hide();
                              }}
                              onOpen={() => {
                                latestActions?.handleAppClick(appId);
                                instance.hide();
                              }}
                              onFocusWindow={(windowId) => {
                                latestActions?.focusWindow(windowId);
                                instance.hide();
                              }}
                              canPin={true}
                              isPinned={isPinned}
                              onTogglePin={() => {
                                if (isPinned) {
                                  unpinApp(appId);
                                } else {
                                  pinApp(appId);
                                }
                                instance.hide();
                              }}
                              onHideFromDock={
                                appIsOpen
                                  ? undefined
                                  : () => {
                                      hideApp(appId);
                                      instance.hide();
                                    }
                              }
                            />,
                          );
                        },
                        onHide() {
                          if (currentRoot) {
                            currentRoot.unmount();
                            currentRoot = null;
                          }
                        },
                        onClickOutside(instance) {
                          instance.hide();
                        },
                      });

                      el.addEventListener("contextmenu", (e: Event) => {
                        e.preventDefault();
                        e.stopPropagation();
                        contextMenuInstance.show();
                      });

                      el.setAttribute("data-context-menu-initialized", "true");
                    }
                  }}
                  onClick={() => handleAppClick(appId)}
                  onMouseEnter={() => {
                    void preloadOsApp(appId);
                  }}
                  onPointerDown={() => {
                    void preloadOsApp(appId);
                  }}
                  className={`${DOCK_BUTTON_BASE_CLASS} ${getDockButtonStateClasses()}`}
                >
                  {(() => {
                    const { classes, style } = getDockIconBackgroundClasses(
                      app.colorTheme,
                      false,
                      dockIconStyle,
                    );
                    return (
                      <div
                        className={`${DOCK_ICON_TILE_CLASS} cursor-pointer ${classes}`}
                        style={style}
                      >
                        {app.icon}
                      </div>
                    );
                  })()}

                  {appIsOpen ? (
                    <div
                      className={`absolute -bottom-[6px] h-[3px] w-3 rounded-full ${
                        appIsFocused
                          ? DOCK_OPEN_INDICATOR_ACTIVE_CLASS
                          : DOCK_OPEN_INDICATOR_IDLE_CLASS
                      }`}
                    />
                  ) : null}
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {/* Divider */}
        <motion.div
          layout
          className="h-8 w-px shrink-0 bg-slate-200/50 dark:bg-slate-500/50"
          transition={{
            layout: { duration: 0.3, ease: "easeInOut" },
          }}
        />

        {/* Apps Button */}
        <motion.div
          layout
          className="relative"
          transition={{
            layout: { duration: 0.3, ease: "easeInOut" },
          }}
        >
          {appsPopoverOpen && (
            <AppsDockPopover
              onClose={() => setAppsPopoverOpen(false)}
              buttonRef={appsButtonRef}
              dockIconStyle={dockIconStyle}
              excludeAppIds={
                isOpenworkCompany(companyId)
                  ? [...dockAppIds, "home"]
                  : dockAppIds
              }
              companyId={companyId}
            />
          )}
          <button
            type="button"
            data-testid="os-dock-apps"
            ref={(el) => {
              if (el && !el.hasAttribute("data-tippy-initialized")) {
                tippy(el, {
                  content: "Apps",
                  delay: [300, 0],
                  placement: "top",
                  arrow: false,
                  animation: "shift-away",
                  theme: "dock",
                  offset: [0, 10],
                });
                el.setAttribute("data-tippy-initialized", "true");
              }
              appsButtonRef.current = el;
            }}
            onClick={() => {
              setAppsPopoverOpen((prev) => !prev);
              appsButtonRef.current?.blur();
            }}
            className={`${DOCK_BUTTON_BASE_CLASS} ${getDockButtonStateClasses()}`}
          >
            <div
              className={`${DOCK_ICON_TILE_CLASS} ${DOCK_TILE_SHELL_CLASSES} text-slate-600 dark:text-slate-300 transition-shadow group-hover:shadow-md`}
            >
              <FontAwesomeIcon icon={faGrid2} className="text-xl" />
            </div>
          </button>
        </motion.div>

        {/* Ani Assistant — far right; styling restored from pre–window-handling dock (619ec88a) */}
        {onAniClick && hasModule("ani") ? (
          <motion.button
            type="button"
            layout
            transition={{
              layout: { duration: 0.3, ease: "easeInOut" },
            }}
            data-testid="os-dock-ani"
            aria-label="Ani Assistant"
            aria-expanded={isAniOpen}
            ref={(el) => {
              if (el && !el.hasAttribute("data-tippy-initialized")) {
                tippy(el, {
                  content: `Ani Assistant (${getShortcutLabel("TOGGLE_ANI_ASSISTANT", isMac)})`,
                  delay: [300, 0],
                  placement: "top",
                  arrow: false,
                  animation: "shift-away",
                  theme: "dock",
                  offset: [0, 16],
                });
                el.setAttribute("data-tippy-initialized", "true");
              }
              aniButtonRef.current = el;
            }}
            onClick={() => {
              if (showAniPulse) {
                markAniDiscoverabilitySeen();
                setShowAniPulse(false);
              }
              onAniClick();
            }}
            className={`group relative flex cursor-pointer items-center justify-center rounded-full outline-none transition-all duration-200 focus:outline-none focus-visible:outline-none ${
              showAniPulse && isOpenworkCompany(companyId) && !isAniOpen
                ? "os-dock-ani-pulse"
                : ""
            }`}
          >
            <div className="w-12 h-12 flex items-center justify-center">
              <img
                src={`${import.meta.env.BASE_URL}images/logo.svg`}
                alt=""
                width={40}
                height={40}
                className="w-10 h-10 object-contain pointer-events-none"
              />
            </div>
          </motion.button>
        ) : null}
      </motion.div>

    </div>
  );
});
